// /home/coder/project/frontend/src/pages/qaqc/inspections/QaqcInspFormsList.tsx
import React, { useMemo, useState } from 'react'; // Added useMemo and useState
import { Spin, Card, message, Typography, Popconfirm, Space } from 'antd'; // Removed Tag
import StandardTable from '../../../components/common/StandardTable';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGetVwQaqcInspFormDetails } from '../../../hooks/genericHooks'; // Changed to the correct hook for form design details
import { deleteData } from '../../../services/api';
import type { VwQaqcInspFormDetails, QaqcInspFormDesigns } from '../../../types'; // Changed to appropriate types
import { PlusOutlined, AuditOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const QaqcInspFormsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingFormDesign, setEditingFormDesign] = useState<QaqcInspFormDesigns | null>(null);

  // --- Fetch Data ---
  // Fetch data from the view vw_qaqc_insp_formdetails
  const { data: formDetailsData, isLoading, error } = useGetVwQaqcInspFormDetails();
  const allFormDetails = formDetailsData?.data || [];

  // --- Process Data for List ---
  // Group the details by design_id to get unique form designs for the list
  const uniqueFormDesigns = useMemo(() => {
    const designsMap = new Map<number, { id: number; form_num: string; form_name: string; field_count: number; created_at?: string }>();
    allFormDetails.forEach(detail => {
      if (!designsMap.has(detail.design_id)) {
        // VwQaqcInspFormDetails might not have created_at, removed for now.
        designsMap.set(detail.design_id, { id: detail.design_id, form_num: detail.form_num, form_name: detail.form_name, field_count: 0 });
      }
      designsMap.get(detail.design_id)!.field_count++; // Increment field count
    });
    return Array.from(designsMap.values());
  }, [allFormDetails]);

  // --- Delete Mutation ---
  const deleteFormDesignMutation = useMutation<unknown, Error, number>({
    mutationFn: (id: number) => deleteData('qaqc_insp_form_designs', 'id', id), // Target qaqc_insp_form_designs table
    onSuccess: (_, id) => {
      message.success(`QA/QC Form Design (ID: ${id}) deleted successfully!`);
      queryClient.invalidateQueries({ queryKey: ['vw_qaqc_insp_formdetails'] }); // Invalidate the view query
    },
    onError: (error: any, id) => {
      console.error(`Error deleting QA/QC Form Design (ID: ${id}):`, error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to delete QA/QC Form Design.';
      message.error(`Error: ${errorMsg}`);
    },
  });

  // --- Event Handlers ---
  const handleDelete = (id: number) => {
    deleteFormDesignMutation.mutate(id);
  };

  const handleAdd = () => {
    setEditingFormDesign(null);
    navigate('/qaqc/inspections/form_design'); // Navigate to QA/QC form design creation page
  };

  const handleEdit = (record: { id: number; form_num: string; form_name: string; field_count: number }) => {
    navigate(`/qaqc/inspections/edit/${record.id}`); // Navigate to edit page for form designs
  };

  const handleView = (record: { id: number; form_num: string; form_name: string; field_count: number }) => {
    navigate(`/qaqc/inspections/show/${record.id}`); // Navigate to detail page for form designs
  };

  // --- Table Columns ---
  const columns = [
    {
      title: 'Form No.',
      dataIndex: 'form_num',
      key: 'form_num',
      sorter: (a: { form_num: string }, b: { form_num: string }) => a.form_num.localeCompare(b.form_num),
    },
    {
      title: 'Form Name',
      dataIndex: 'form_name',
      key: 'form_name',
      sorter: (a: { form_name: string }, b: { form_name: string }) => a.form_name.localeCompare(b.form_name),
    },
    {
      title: '# Fields',
      dataIndex: 'field_count',
      key: 'field_count',
      sorter: (a: { field_count: number }, b: { field_count: number }) => a.field_count - b.field_count,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: { id: number; form_num: string; form_name: string; field_count: number }) => {
        const designId = record.id;
        return (
          <Space>
            <Button
              action="read"
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              size="small"
              title="View Form Design Details"
            />
            <Button
              action="edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              title="Edit Form Design"
            />
            <Popconfirm
              title="Are you sure you want to delete this QA/QC form design?"
              onConfirm={() => handleDelete(designId)}
              okText="Yes"
              cancelText="No"
              disabled={deleteFormDesignMutation.isPending && deleteFormDesignMutation.variables === designId}
            >
              <Button action="delete" type="link" danger icon={<DeleteOutlined />} loading={deleteFormDesignMutation.isPending && deleteFormDesignMutation.variables === designId} size="small" title="Delete Form Design" />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // --- Render Logic ---
  if (error) {
    message.error(`Error fetching QA/QC Form Designs: ${error.message}`);
  }

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          <AuditOutlined style={{ marginRight: 8 }} />
          QA/QC Inspection Form Designs
        </Title>
      }
      bordered={false}
      extra={
        <Button
          action="add"
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Add New QA/QC Form Design
        </Button>
      }
    >
      <Spin spinning={isLoading || deleteFormDesignMutation.isPending}>
        <StandardTable
          columns={columns}
          dataSource={uniqueFormDesigns} // Use the processed unique designs
          rowKey="id" // 'id' here refers to design_id from the map
          bordered
          pagination={{ pageSize: 20 }}
          exportFileName="qaqc_inspection_form_designs"
        />
      </Spin>
    </Card>
  );
};

export default QaqcInspFormsList;